import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Tag, Package, Tractor, Paperclip, FileText, Image, Sheet, Anchor } from "lucide-react";
import DateTimePicker from "../components/ui/DateTimePicker";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:4040/api";
const CUSTOMERS_API = `${API_BASE}/customers`;
const RATES_API = `${API_BASE}/rates`;
const SETTINGS_API = `${API_BASE}/settings`;

const INPUT_CLASS = "w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-900 outline-none transition focus:border-slate-950 focus:ring-2 focus:ring-slate-950/10";

const BOOKING_TYPES = [
  { value: "freight", label: "Freight", Icon: Package, desc: "Pallets, containers, break bulk" },
  { value: "livestock", label: "Livestock", Icon: Tag, desc: "Cattle, sheep, deer" },
  { value: "machinery", label: "Machinery", Icon: Tractor, desc: "Vehicles, plant, equipment" },
];

const emptyFreightItem = () => ({ description: "", unitType: "pallet", quantity: "", weightKg: "", lengthM: "", widthM: "", heightM: "", cubeM3: "", temperatureControl: "ambient", notes: "", packingMode: "single", packingRows: [] });

const emptyPackingRow = () => ({ type: "pallet", description: "", qty: "", weightKg: "", lengthM: "", widthM: "", heightM: "" });

const DRAFT_STORAGE_KEY = "shipstock_booking_draft";

// Whether a form actually has something worth saving/restoring — an
// untouched, freshly-opened form shouldn't count, otherwise the very act
// of loading the page creates a "draft" that then gets detected as
// something to restore on the next visit, even though nothing was ever
// actually entered.
const isFormMeaningful = (f) => {
  if (!f) return false;
  const hasFreightData = (f.freightItems || []).some(
    i => i.description || i.weightKg || i.quantity || (i.packingRows || []).some(r => r.qty || r.weightKg)
  );
  const hasLivestockData = (f.livestockItems || []).some(i => i.headCount);
  const hasMachineryData = f.machineryDescription;
  const hasSenderData = f.senderName || f.senderCompany;
  return hasFreightData || hasLivestockData || hasMachineryData || hasSenderData;
};
const emptyLivestockItem = () => ({
  livestockType: "cattle", animalClass: "", headCount: "", avgWeightKg: "",
  lastFed: "", lastWatered: "", specialStatus: "", biosecurityMeasures: "", notes: "",
});

export default function BookingRequestPage({ embedded = false, fixedCustomerId = "", onSubmitted = null, editBookingId = "", initialData = null } = {}) {
  const [searchParams] = useSearchParams();
  const customerId = fixedCustomerId || searchParams.get("customerId") || "";
  // A sailingId present in the URL means the customer arrived here by clicking
  // "Request Booking" on a specific sailing (e.g. from the Sailing Schedule) —
  // in that case the sailing choice is already made and shown read-only,
  // rather than asking them to pick it again from a dropdown.
  const lockedSailingId = searchParams.get("sailingId") || "";

  const [sailings, setSailings] = useState([]);
  const [rates, setRates] = useState([]);
  const [settings, setSettings] = useState(null);
  const [submittedRef, setSubmittedRef] = useState(null);
  const [submittedId, setSubmittedId] = useState(null);
  const [redirectingToPayment, setRedirectingToPayment] = useState(false);
  // Detects a return trip from Stripe Checkout — set once on load, doesn't
  // need to react to later changes, so reading searchParams directly here
  // (rather than in a useEffect) is fine.
  const paymentResult = searchParams.get("payment"); // "success" | "cancelled" | null
  const paymentRef = searchParams.get("ref");
  const [submitting, setSubmitting] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [agreedToOwnersRisk, setAgreedToOwnersRisk] = useState(false);
  const [ownersRiskName, setOwnersRiskName] = useState("");
  const [error, setError] = useState("");
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [loggedInCustomer, setLoggedInCustomer] = useState(null);
  const [loadingCustomer, setLoadingCustomer] = useState(Boolean(customerId));

  useEffect(() => {
    if (!customerId) return;
    (async () => {
      try {
        setLoadingCustomer(true);
        const res = await fetch(`${CUSTOMERS_API}/${customerId}`);
        if (!res.ok) return;
        const data = await res.json();
        const customer = data?.data || data;
        if (!customer) return;
        setLoggedInCustomer(customer);

        // Prefill sender fields from the logged-in customer's profile.
        setForm(prev => ({
          ...prev,
          senderName: customer.contactName || prev.senderName,
          senderCompany: customer.companyName || prev.senderCompany,
          senderPhone: customer.phone || prev.senderPhone,
          senderEmail: customer.email || prev.senderEmail,
          senderAddress: [customer.address?.line1, customer.address?.city].filter(Boolean).join(", ") || prev.senderAddress,
        }));
      } catch (err) {
        console.error("Failed to load customer profile:", err);
      } finally {
        setLoadingCustomer(false);
      }
    })();
  }, [customerId]);

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    setUploading(true);
    const results = [];

    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append("images", file);
        const res = await fetch(`${API_BASE}/upload/images`, {
          method: "POST",
          body: formData,
        });
        if (!res.ok) throw new Error("Upload failed");
        const url = await res.json();
        results.push({
          name: file.name,
          size: file.size,
          type: file.type,
          url: typeof url === "string" ? url : URL.createObjectURL(file),
          category: guessCategory(file.name),
        });
      } catch {
        results.push({
          name: file.name,
          size: file.size,
          type: file.type,
          url: null,
          error: "Upload failed",
          category: guessCategory(file.name),
        });
      }
    }

    setUploadedFiles(prev => [...prev, ...results]);
    setUploading(false);
    e.target.value = "";
  };

  const removeFile = (index) => setUploadedFiles(prev => prev.filter((_, i) => i !== index));

  const [draftRestored, setDraftRestored] = useState(false);

  const [form, setForm] = useState(() => {
    const base = {
      bookingType: "freight",
      sailingId: searchParams.get("sailingId") || "",
      origin: "Auckland",
      destination: "Napier",
      routeOverride: false,
      shippingMode: "Port to Port",

      // Contact
      senderName: "",
      senderCompany: "",
      senderPhone: "",
      senderEmail: "",
      senderAddress: "",
      agentName: "",
      agentPhone: "",
      agentEmail: "",

      // Receiver
      receiverName: "",
      receiverCompany: "",
      receiverPhone: "",
      receiverEmail: "",
      deliveryAddress: "",

      // Emergency
      emergencyContact: "",
      emergencyPhone: "",
      vetEmergencyPhone: "",

      // Livestock — declaration fields apply once to the whole consignment;
      // the actual animal groups are a repeatable list (see livestockItems).
      fitForTransport: false,
      vetCertificate: false,

      // Livestock line items (array) — one entry per animal group, e.g.
      // "20 cattle" and "20 sheep" as two separate entries in the same booking.
      // Special status, biosecurity measures and notes live per-group here too,
      // since they can genuinely differ between e.g. cattle and sheep in the
      // same consignment.
      livestockItems: [emptyLivestockItem()],

      // Freight items (array)
      freightItems: [emptyFreightItem()],

      // Machinery
      machineryDescription: "",
      machineryMake: "",
      machineryModel: "",
      machineryWeightKg: "",
      machineryLengthM: "",
      machineryWidthM: "",
      machineryHeightM: "",
      machineryRoRo: true,
      machineryHazardous: false,
      machineryNotes: "",

      notes: "",
    };

    if (!initialData) {
      // Only for new bookings, not editing an existing one — restores
      // whatever was last saved, so a customer who gets sent to Stripe and
      // hits browser back doesn't lose everything they already filled in.
      try {
        const saved = localStorage.getItem(DRAFT_STORAGE_KEY);
        if (saved) {
          const parsed = JSON.parse(saved);
          if (parsed && typeof parsed === "object" && isFormMeaningful(parsed)) return { ...base, ...parsed };
        }
      } catch (e) {
        // Corrupted/unreadable draft — fall through to a clean form rather
        // than breaking the page.
      }
      return base;
    }

    // Map a saved booking record back into this form's flat shape for editing.
    const lv = initialData.livestockItems?.[0] || {};
    const sender = initialData.onlineRequest?.sender || {};
    const agent = initialData.onlineRequest?.agent || {};
    const receiver = initialData.onlineRequest?.receiver || {};
    const emergency = initialData.onlineRequest?.emergency || {};

    return {
      ...base,
      bookingType: initialData.bookingType || base.bookingType,
      sailingId: initialData.sailing?._id || initialData.sailing || "",
      origin: initialData.origin || base.origin,
      destination: initialData.destination || base.destination,
      shippingMode: initialData.shippingMode || base.shippingMode,

      senderName: sender.name || "",
      senderCompany: sender.company || "",
      senderPhone: sender.phone || "",
      senderEmail: sender.email || "",
      senderAddress: sender.address || "",
      agentName: agent.name || "",
      agentPhone: agent.phone || "",
      agentEmail: agent.email || "",

      receiverName: receiver.name || "",
      receiverCompany: receiver.company || "",
      receiverPhone: receiver.phone || "",
      receiverEmail: receiver.email || "",
      deliveryAddress: receiver.address || "",

      emergencyContact: emergency.contact || "",
      emergencyPhone: emergency.phone || "",
      vetEmergencyPhone: emergency.vetPhone || "",

      fitForTransport: Boolean(lv.fitForTransport),
      vetCertificate: Boolean(lv.vetCertificateUploaded),

      livestockItems: initialData.livestockItems?.length
        ? initialData.livestockItems.map(item => ({
            livestockType: item.livestockType || "cattle",
            animalClass: item.animalClass || "",
            headCount: item.headCount != null ? String(item.headCount) : "",
            avgWeightKg: item.averageWeightKg != null ? String(item.averageWeightKg) : "",
            lastFed: item.lastFed ? new Date(item.lastFed).toISOString().slice(0, 16) : "",
            lastWatered: item.lastWatered ? new Date(item.lastWatered).toISOString().slice(0, 16) : "",
            // notes on the saved record combines special status / biosecurity / free text
            // with " | " separators (see handleSubmit) — split back out isn't reliable,
            // so on edit these land in the free-text notes field rather than being
            // re-separated into their own inputs.
            specialStatus: "",
            biosecurityMeasures: "",
            notes: item.notes || "",
          }))
        : base.livestockItems,

      freightItems: initialData.freightItems?.length
        ? initialData.freightItems.map(f => ({
            description: f.description || "",
            unitType: f.freightType || f.unitType || "pallet",
            quantity: f.quantity != null ? String(f.quantity) : "",
            weightKg: f.weightKg != null ? String(f.weightKg) : "",
            lengthM: f.lengthM != null ? String(f.lengthM) : "",
            widthM: f.widthM != null ? String(f.widthM) : "",
            heightM: f.heightM != null ? String(f.heightM) : "",
            cubeM3: f.cubeM3 != null ? String(f.cubeM3) : "",
            temperatureControl: f.temperatureControl || "ambient",
            notes: f.notes || "",
            packingMode: f.packingRows?.length ? "multiple" : "single",
            packingRows: (f.packingRows || []).map(r => ({
              type: r.type || "pallet",
              description: r.description || "",
              qty: r.qty != null ? String(r.qty) : "",
              weightKg: r.weightKg != null ? String(r.weightKg) : "",
              lengthM: r.lengthM != null ? String(r.lengthM) : "",
              widthM: r.widthM != null ? String(r.widthM) : "",
              heightM: r.heightM != null ? String(r.heightM) : "",
            })),
          }))
        : base.freightItems,

      machineryDescription: initialData.machinery?.description || "",
      machineryMake: initialData.machinery?.make || "",
      machineryModel: initialData.machinery?.model || "",
      machineryWeightKg: initialData.machinery?.weightKg ? String(initialData.machinery.weightKg) : "",
      machineryLengthM: initialData.machinery?.lengthM ? String(initialData.machinery.lengthM) : "",
      machineryWidthM: initialData.machinery?.widthM ? String(initialData.machinery.widthM) : "",
      machineryHeightM: initialData.machinery?.heightM ? String(initialData.machinery.heightM) : "",
      machineryRoRo: initialData.machinery?.roRo ?? true,
      machineryHazardous: Boolean(initialData.machinery?.hazardous),
      machineryNotes: initialData.machinery?.notes || "",

      notes: initialData.notes || "",
    };
  });

  useEffect(() => {
    if (initialData) return;
    try {
      const saved = localStorage.getItem(DRAFT_STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (isFormMeaningful(parsed)) setDraftRestored(true);
      }
    } catch (e) {}
    // Only run once on mount — this just detects whether we started from a
    // restored draft, it shouldn't re-fire as the form itself changes.
  }, []);

  // Keeps the in-progress form saved to the browser's local storage, so
  // navigating away (e.g. sent to Stripe checkout, then hitting browser
  // back) restores everything instead of leaving a blank form. Skipped
  // entirely when editing an existing booking — that's real saved data,
  // not a draft, and shouldn't get mixed up with this mechanism.
  useEffect(() => {
    if (initialData) return;
    if (!isFormMeaningful(form)) return;
    try {
      localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(form));
    } catch (e) {
      // Storage full/unavailable (e.g. private browsing) — not worth
      // failing the whole form over, just skip saving this time.
    }
  }, [form, initialData]);

  useEffect(() => {
    fetch(`${API_BASE}/sailings?status=open`)
      .then(r => r.json())
      .then(data => setSailings(Array.isArray(data) ? data : (data?.data || [])))
      .catch(() => {});

    fetch(RATES_API)
      .then(r => r.json())
      .then(data => setRates(Array.isArray(data) ? data : (data?.data || [])))
      .catch(() => {});

    fetch(SETTINGS_API)
      .then(r => r.json())
      .then(data => setSettings(data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!lockedSailingId || sailings.length === 0) return;
    const locked = sailings.find(s => s._id === lockedSailingId);
    if (locked) {
      setForm(prev => ({ ...prev, origin: locked.origin || prev.origin, destination: locked.destination || prev.destination }));
    }
  }, [lockedSailingId, sailings]);

  const update = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  const updateSailing = (sailingId) => {
    const selected = sailings.find(s => s._id === sailingId);
    setForm(prev => ({
      ...prev,
      sailingId,
      origin: selected?.origin || prev.origin,
      destination: selected?.destination || prev.destination,
    }));
  };

  const clearSailing = () => {
    setForm(prev => ({ ...prev, sailingId: "", routeOverride: false }));
  };

  const updateFreightItem = (index, key, value) => {
    setForm(prev => {
      const items = [...prev.freightItems];
      const updated = { ...items[index], [key]: value };
      // Auto-calculate cube from Length × Width × Height whenever any
      // dimension changes. This does NOT multiply by quantity — matches
      // how Weight (kg) already works elsewhere on this form, where the
      // value entered is treated as the item's total, not a per-unit
      // figure scaled up automatically. Mixing the two conventions (cube
      // scaled by qty, weight not) is what made the cube number look
      // inflated/wrong compared to what people expected.
      if (["lengthM", "widthM", "heightM"].includes(key)) {
        const l = Number(updated.lengthM || 0);
        const w = Number(updated.widthM || 0);
        const h = Number(updated.heightM || 0);
        const cube = l * w * h;
        updated.cubeM3 = cube > 0 ? String(Number(cube.toFixed(3))) : "";
      }
      items[index] = updated;
      return { ...prev, freightItems: items };
    });
  };

  // Packing list mode — for items where individual pieces vary in size
  // (e.g. 7 packets of different lengths). Each row is qty × its own
  // weight/dimensions; rows sum into a single effective total weight/cube
  // for the item, which is what actually feeds pricing (same as
  // single-item mode) — this is purely a different way to arrive at that
  // total, not a second pricing path.
  const setPackingMode = (index, mode) => {
    setForm(prev => {
      const items = [...prev.freightItems];
      const item = items[index];
      items[index] = {
        ...item,
        packingMode: mode,
        packingRows: mode === "multiple" && item.packingRows.length === 0 ? [emptyPackingRow()] : item.packingRows,
      };
      return { ...prev, freightItems: items };
    });
  };

  const addPackingRow = (itemIndex) => {
    setForm(prev => {
      const items = [...prev.freightItems];
      items[itemIndex] = { ...items[itemIndex], packingRows: [...items[itemIndex].packingRows, emptyPackingRow()] };
      return { ...prev, freightItems: items };
    });
  };

  const removePackingRow = (itemIndex, rowIndex) => {
    setForm(prev => {
      const items = [...prev.freightItems];
      items[itemIndex] = { ...items[itemIndex], packingRows: items[itemIndex].packingRows.filter((_, i) => i !== rowIndex) };
      return { ...prev, freightItems: items };
    });
  };

  const updatePackingRow = (itemIndex, rowIndex, key, value) => {
    setForm(prev => {
      const items = [...prev.freightItems];
      const rows = [...items[itemIndex].packingRows];
      rows[rowIndex] = { ...rows[rowIndex], [key]: value };
      items[itemIndex] = { ...items[itemIndex], packingRows: rows };
      return { ...prev, freightItems: items };
    });
  };

  // Sums a packing list into a single effective weight/cube — each row's
  // weight/dimensions are per-piece, multiplied by that row's qty.
  const packingRowTotals = (rows) => {
    return (rows || []).reduce((acc, r) => {
      const qty = Number(r.qty || 0);
      const w = Number(r.weightKg || 0);
      const l = Number(r.lengthM || 0);
      const wd = Number(r.widthM || 0);
      const h = Number(r.heightM || 0);
      acc.weightKg += qty * w;
      acc.cubeM3 += qty * l * wd * h;
      return acc;
    }, { weightKg: 0, cubeM3: 0 });
  };

  // Returns the effective weight/cube for an item regardless of mode —
  // single-item's own flat fields, or the summed packing rows.
  const effectiveItemTotals = (item) => {
    if (item.packingMode === "multiple") return packingRowTotals(item.packingRows);
    return { weightKg: Number(item.weightKg || 0), cubeM3: Number(item.cubeM3 || 0) };
  };

  const updateLivestockItem = (index, key, value) => {
    setForm(prev => {
      const items = [...prev.livestockItems];
      items[index] = { ...items[index], [key]: value };
      return { ...prev, livestockItems: items };
    });
  };

  // Finds the best matching rate for a given type + free-text name.
  // Prefers a rate whose route matches the current origin/destination leg,
  // falls back to a route-agnostic ("Any") rate of the same type.
  const findRate = (type, nameHint, route) => {
    const activeRates = rates.filter(r => r.active && r.type === type);
    const byRoute = route
      ? activeRates.filter(r => r.route && r.route !== "Any" && route.toLowerCase().includes(r.route.split(" - ")[0].toLowerCase()))
      : [];
    const pool = byRoute.length ? byRoute : activeRates.filter(r => !r.route || r.route === "Any");
    if (!nameHint) return null;
    const lower = nameHint.toLowerCase();
    return (
      pool.find(r => r.name.toLowerCase() === lower) ||
      pool.find(r => lower.includes(r.name.toLowerCase()) || r.name.toLowerCase().includes(lower)) ||
      null
    );
  };

  // Computes a charge amount for a given rate + item quantity/weight/cube.
  // Handles every unit in the Rate schema, and applies the rate's
  // minimumCharge floor if the calculated amount comes out below it.
  // Looks up the Chilled/Frozen rate for a temperature-controlled item.
  // These are configured as their own per-m³ rate (type: surcharge, unit:
  // per_m3) and REPLACE the item's normal freight charge entirely — reefer
  // freight is priced by the space it occupies, not as a markup on general
  // cargo rates.
  const findTemperatureRate = (temperatureControl) => {
    if (!temperatureControl || temperatureControl === "ambient") return null;
    return rates.find(
      r => r.active && r.type === "surcharge" && r.name.toLowerCase() === temperatureControl.toLowerCase()
    ) || null;
  };

  const priceForRate = (rate, { qty = 1, weightKg = 0, cubeM3 = 0 } = {}) => {
    if (!rate) return 0;
    const price = Number(rate.price || 0);
    const minimum = Number(rate.minimumCharge || 0);
    let amount = 0;
    switch (rate.unit) {
      case "per_kg":
        amount = weightKg > 0 ? weightKg * price : 0;
        break;
      case "per_tonne":
        amount = weightKg > 0 ? (weightKg / 1000) * price : 0;
        break;
      case "per_m3":
        amount = cubeM3 > 0 ? cubeM3 * price : 0;
        break;
      case "flat":
      case "per_booking":
        amount = price;
        break;
      case "per_item":
      case "per_container":
      case "per_pallet":
      case "per_unit":
      case "per_head":
      default:
        amount = (qty || 1) * price;
    }
    if (amount > 0 && minimum > 0 && amount < minimum) amount = minimum;
    return amount;
  };

  const estimate = useMemo(() => {
    const routeLeg = `${form.origin} ${form.destination}`;
    const lines = [];

    // Council Levy — a separate charge on top of freight/livestock, only
    // applicable to Waitangi/Chatham Islands routes (configured via each
    // levy rate's own Route field in Rate Settings, e.g. "Napier - Chatham
    // Islands"). findRate already respects route matching, so this only
    // fires when the booking's actual route matches a configured levy.
    const addLevyLine = (nameHints, qtyOpts, itemLabel) => {
      let levyRate = null;
      for (const hint of nameHints) {
        levyRate = findRate("levy", hint, routeLeg);
        if (levyRate) break;
      }
      if (!levyRate) return;
      const amount = priceForRate(levyRate, qtyOpts);
      if (amount > 0) lines.push({ label: "Chatham Islands Council Levy", amount, rateName: levyRate.name, isLevy: true });
    };

    if (form.bookingType === "livestock") {
      (form.livestockItems || []).forEach(item => {
        const head = Number(item.headCount || 0);
        const weight = Number(item.avgWeightKg || 0);
        if (!head) return;
        const rate = findRate("livestock", item.livestockType, routeLeg);
        if (rate) {
          const amount = priceForRate(rate, { qty: head, weightKg: head * weight });
          lines.push({ label: `${item.livestockType} (${head} head)`, amount, rateName: rate.name });
        }
        addLevyLine([item.livestockType], { qty: head, weightKg: head * weight }, item.livestockType);
      });
    }

    if ((form.bookingType === "freight" || form.bookingType === "machinery") ) {
      (form.freightItems || []).forEach(item => {
        const packingTotals = effectiveItemTotals(item);
        const hasPackingData = item.packingMode === "multiple" && (packingTotals.weightKg > 0 || packingTotals.cubeM3 > 0);
        if (!item.description && !item.quantity && !hasPackingData) return;
        const qty = item.packingMode === "multiple"
          ? (item.packingRows || []).reduce((s, r) => s + Number(r.qty || 0), 0) || 1
          : Number(item.quantity || 1);
        const { weightKg: weight, cubeM3: cube } = packingTotals;
        const label = item.description || item.unitType;
        const levyHints = [item.description, item.unitType, "general cargo"];

        // Chilled/Frozen: charge purely at cube × the item-specific rate,
        // ignoring whatever the normal freight rate would have been. The
        // council levy still applies on top, independent of the freight
        // pricing model used.
        const tempRate = findTemperatureRate(item.temperatureControl);
        if (tempRate) {
          let amount = cube > 0 ? cube * Number(tempRate.price || 0) : 0;
          const tempMinimum = Number(tempRate.minimumCharge || 0);
          if (amount > 0 && tempMinimum > 0 && amount < tempMinimum) amount = tempMinimum;
          if (amount > 0) {
            lines.push({ label: `${label} (${item.temperatureControl}, ${cube}m³ @ $${tempRate.price}/m³)`, amount, rateName: tempRate.name });
          }
          addLevyLine(levyHints, { qty, weightKg: weight, cubeM3: cube }, label);
          return;
        }

        const rate =
          findRate("freight", item.description, routeLeg) ||
          findRate("freight", item.unitType, routeLeg) ||
          findRate("freight", "freight", routeLeg) ||
          findRate("other", item.description, routeLeg);

        if (!rate) {
          lines.push({ label: `${label} — no matching rate configured, contact us for a quote`, amount: 0, rateName: null, warning: true });
          return;
        }
        const amount = priceForRate(rate, { qty, weightKg: weight, cubeM3: cube });
        if (amount > 0) lines.push({ label, amount, rateName: rate.name });
        addLevyLine(levyHints, { qty, weightKg: weight, cubeM3: cube }, label);
      });
    }

    // FAF applies only to freight/livestock charges, not the Council Levy
    // (a fixed port due, not a fuel-scaled freight cost) — matches
    // invoice.route.js / stripe.route.js's calculation order. Uses an
    // explicit isLevy flag rather than checking the line's label text,
    // since a label rename (e.g. "Council Levy" → "Council Dues") would
    // otherwise silently break this exclusion without any error.
    const fafBase = lines.filter(l => !l.isLevy).reduce((sum, l) => sum + l.amount, 0);

    const accessorialEnabled = settings?.rates?.accessorialChargesEnabled !== false;
    const fafPercent = accessorialEnabled ? Number(settings?.rates?.fuelAdjustmentFactorPercent || 0) : 0;
    const fafAmount = fafPercent > 0 ? fafBase * (fafPercent / 100) : 0;

    if (fafAmount > 0) {
      lines.push({ label: `Fuel Adjustment Factor (${fafPercent}%)`, amount: fafAmount, rateName: "FAF" });
    }

    const subtotal = lines.reduce((sum, l) => sum + l.amount, 0);

    // GST — matches invoice.route.js / stripe.route.js's fixed GST_RATE
    // (15%), applied to the full subtotal including FAF and Council Levy.
    // Added last, same order as the backend, so this estimate lands on the
    // exact same total a customer will see at Stripe checkout.
    const GST_RATE = 0.15;
    const gstAmount = subtotal * GST_RATE;
    if (gstAmount > 0) {
      lines.push({ label: "GST (15%)", amount: gstAmount, rateName: "GST" });
    }

    const total = subtotal + gstAmount;
    return { lines, total, hasEstimate: lines.length > 0 };
  }, [form.bookingType, form.livestockItems, form.freightItems, form.origin, form.destination, rates, settings]);

  const addFreightItem = () => setForm(prev => ({ ...prev, freightItems: [...prev.freightItems, emptyFreightItem()] }));
  const removeFreightItem = (index) => setForm(prev => ({ ...prev, freightItems: prev.freightItems.filter((_, i) => i !== index) }));

  const addLivestockItem = () => setForm(prev => ({ ...prev, livestockItems: [...prev.livestockItems, emptyLivestockItem()] }));
  const removeLivestockItem = (index) => setForm(prev => ({ ...prev, livestockItems: prev.livestockItems.filter((_, i) => i !== index) }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!form.senderName || !form.senderPhone) {
      setError("Please fill in your name and phone number.");
      return;
    }
    if (form.bookingType === "livestock" && !form.livestockItems.some(item => Number(item.headCount) > 0)) {
      setError("Please enter a head count for at least one animal group.");
      return;
    }
    if (!form.fitForTransport && form.bookingType === "livestock") {
      setError("Fit for transport declaration is required.");
      return;
    }
    if (!agreedToOwnersRisk || !ownersRiskName.trim()) {
      setError("Please read and agree to the Owner's Risk Statement, and type your full name, before submitting.");
      return;
    }
    if (!agreedToTerms) {
      setError("Please confirm you agree to the Terms & Conditions before submitting.");
      return;
    }

    try {
      setSubmitting(true);
      // New bookings no longer generate their own ref client-side — the
      // backend now assigns a sequential MCB-YYYY-#### reference via an
      // atomic counter, so leaving bookingRef out of the payload lets it
      // do that. Editing an existing booking keeps its original ref.
      const ref = editBookingId ? initialData?.bookingRef : undefined;

      const payload = {
        ...(ref ? { bookingRef: ref } : {}),
        source: loggedInCustomer ? "customer_portal" : "online",
        customer: loggedInCustomer?._id || null,
        customerId: loggedInCustomer?._id || null,
        status: "pending",
        ownersRiskStatement: {
          executed: true,
          signedName: ownersRiskName.trim(),
          executedAt: new Date().toISOString(),
          executedVia: "online_booking",
        },
        bookingType: form.bookingType,
        sailing: form.sailingId || null,
        requestedSailingRef: sailings.find(s => s._id === form.sailingId)?.sailingRef || "",
        // Snapshot of the linked sailing's key details at time of booking —
        // was missing entirely from this form's payload (only NewBooking.jsx,
        // the staff-side form, built this), meaning the print page's Sailing
        // section always showed blank/TBC for any booking submitted here.
        sailingSnapshot: (() => {
          const s = sailings.find(sail => sail._id === form.sailingId);
          if (!s) return null;
          return {
            sailingMongoId: s._id || "",
            sailingRef: s.sailingRef || s.sailingId || "",
            vesselName: s.vessel?.name || s.vesselName || "",
            route: s.route || s.routeLabel || `${s.origin || form.origin || "-"} → ${s.destination || form.destination || "-"}`,
            etd: s.etd || "",
            eta: s.eta || "",
            status: s.status || "",
          };
        })(),
        origin: form.origin,
        destination: form.destination,
        shippingMode: form.shippingMode,
        customerName: loggedInCustomer?.companyName || form.senderCompany || form.senderName,

        onlineRequest: {
          sender: {
            name: form.senderName,
            company: form.senderCompany,
            phone: form.senderPhone,
            email: form.senderEmail,
            address: form.senderAddress,
          },
          agent: {
            name: form.agentName,
            phone: form.agentPhone,
            email: form.agentEmail,
          },
          receiver: {
            name: form.receiverName,
            company: form.receiverCompany,
            phone: form.receiverPhone,
            email: form.receiverEmail,
            address: form.deliveryAddress,
          },
          emergency: {
            contact: form.emergencyContact,
            phone: form.emergencyPhone,
            vetPhone: form.vetEmergencyPhone,
          },
        },

        livestockItems: form.bookingType === "livestock"
          ? form.livestockItems
              .filter(item => Number(item.headCount) > 0)
              .map(item => ({
                livestockType: item.livestockType,
                animalClass: item.animalClass,
                headCount: Number(item.headCount || 0),
                averageWeightKg: Number(item.avgWeightKg || 0),
                totalWeightKg: Number(item.headCount || 0) * Number(item.avgWeightKg || 0),
                lastFed: item.lastFed || null,
                lastWatered: item.lastWatered || null,
                // The Fit for Transport / Vet Certificate declaration is made once
                // for the whole consignment (see Farmer Declarations card below),
                // so the same value is applied to every animal group here.
                fitForTransport: form.fitForTransport,
                vetCertificateUploaded: form.vetCertificate,
                notes: [
                  item.specialStatus ? `Special status: ${item.specialStatus}` : "",
                  item.biosecurityMeasures ? `Biosecurity: ${item.biosecurityMeasures}` : "",
                  item.notes,
                ].filter(Boolean).join(" | "),
              }))
          : [],

        freightItems: form.bookingType === "freight" ? form.freightItems
          .filter(i => {
            const totals = effectiveItemTotals(i);
            const hasPackingData = i.packingMode === "multiple" && (totals.weightKg > 0 || totals.cubeM3 > 0);
            return i.description || i.quantity || hasPackingData;
          })
          .map(i => {
            const totals = effectiveItemTotals(i);
            const quantity = i.packingMode === "multiple"
              ? (i.packingRows || []).reduce((s, r) => s + Number(r.qty || 0), 0)
              : Number(i.quantity || 0);
            return {
              freightType: i.unitType,
              description: i.description,
              quantity,
              weightKg: totals.weightKg,
              lengthM: Number(i.lengthM || 0),
              widthM: Number(i.widthM || 0),
              heightM: Number(i.heightM || 0),
              cubeM3: totals.cubeM3,
              temperatureControl: i.temperatureControl || "ambient",
              packingRows: i.packingMode === "multiple"
                ? (i.packingRows || []).map(r => ({
                    type: r.type || "pallet",
                    description: r.description || "",
                    qty: Number(r.qty || 0),
                    weightKg: Number(r.weightKg || 0),
                    lengthM: Number(r.lengthM || 0),
                    widthM: Number(r.widthM || 0),
                    heightM: Number(r.heightM || 0),
                  }))
                : [],
            };
          }) : [],

        machinery: form.bookingType === "machinery" ? {
          description: form.machineryDescription,
          make: form.machineryMake,
          model: form.machineryModel,
          weightKg: Number(form.machineryWeightKg || 0),
          lengthM: Number(form.machineryLengthM || 0),
          widthM: Number(form.machineryWidthM || 0),
          heightM: Number(form.machineryHeightM || 0),
          roRo: form.machineryRoRo,
          hazardous: form.machineryHazardous,
          notes: form.machineryNotes,
        } : null,

        notes: form.notes,
        documents: uploadedFiles.filter(f => f.url).map(f => ({
          name: f.name,
          url: f.url,
          category: f.category,
          uploadedAt: new Date(),
        })),
      };

      const isEditing = Boolean(editBookingId);
      const res = await fetch(
        isEditing ? `${API_BASE}/bookings/${editBookingId}` : `${API_BASE}/bookings`,
        {
          method: isEditing ? "PUT" : "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );

      if (!res.ok) throw new Error(isEditing ? "Update failed. Please try again." : "Submission failed. Please try again.");
      const created = await res.json();
      const newBookingId = created?._id || created?.data?._id || null;
      setSubmittedRef(ref);
      setSubmittedId(newBookingId);
      if (!isEditing) {
        try { localStorage.removeItem(DRAFT_STORAGE_KEY); } catch (e) {}
      }
      if (onSubmitted) onSubmitted(ref);

      // Public (non-portal) submissions require payment upfront via Stripe —
      // portal/logged-in customers stay on the existing invoice-on-account
      // flow instead. Skip this entirely when editing an existing booking.
      if (!embedded && !isEditing && newBookingId) {
        try {
          setRedirectingToPayment(true);
          const checkoutRes = await fetch(`${API_BASE}/stripe/create-checkout-session/${newBookingId}`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
          });
          if (!checkoutRes.ok) throw new Error((await checkoutRes.json())?.message || "Could not start payment");
          const { checkoutUrl } = await checkoutRes.json();
          if (checkoutUrl) {
            window.location.href = checkoutUrl;
            return; // leaving the page — nothing more to do here
          }
        } catch (checkoutErr) {
          // The booking itself is safely saved even if payment couldn't be
          // started — don't lose that, just surface it and fall through to
          // the normal success screen so staff can follow up manually.
          console.error("Failed to start Stripe checkout:", checkoutErr);
          setError("Your booking was saved, but we couldn't start the payment step. Our team will be in touch to arrange payment.");
          setRedirectingToPayment(false);
        }
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // Customer has just come back from Stripe Checkout — show a clear result
  // screen rather than an empty booking form. Note the *webhook* (server to
  // server, in stripe.route.js) is the real source of truth for whether the
  // booking actually got marked paid — this redirect could in rare cases
  // fire without a webhook completing yet (e.g. very slow network), so the
  // wording below is deliberately a little cautious rather than absolute.
  if (paymentResult === "success" || paymentResult === "cancelled") {
    const isSuccess = paymentResult === "success";
    const resultBody = (
      <div className={embedded ? "w-full rounded-3xl border border-slate-200 bg-white p-10 text-center" : "w-full max-w-lg rounded-3xl border border-slate-200 bg-white p-10 text-center shadow-sm"}>
        <div className={`mx-auto flex h-16 w-16 items-center justify-center rounded-full ${isSuccess ? "bg-emerald-100" : "bg-amber-100"}`}>
          {isSuccess ? (
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
          ) : (
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#b45309" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
          )}
        </div>
        <h1 className="mt-6 text-2xl font-bold text-slate-950">{isSuccess ? "Payment Successful" : "Payment Not Completed"}</h1>
        <p className="mt-2 text-sm text-slate-500">
          {isSuccess
            ? "Thank you — your payment has been received and your booking is confirmed."
            : "Your booking request has been saved, but payment wasn't completed. You can try again, or contact us to arrange payment another way."}
        </p>
        {paymentRef && (
          <div className="mt-6 rounded-2xl border border-slate-200 bg-slate-50 p-5 text-left">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Reference Number</p>
            <p className="mt-1 text-xl font-bold text-slate-950">{paymentRef}</p>
          </div>
        )}
        {!embedded && (
          <p className="mt-4 text-xs text-slate-400">Questions? Call us on (09) 828 5202.</p>
        )}
      </div>
    );

    if (embedded) return resultBody;
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        {resultBody}
      </div>
    );
  }

  if (redirectingToPayment) {
    const redirectBody = (
      <div className={embedded ? "w-full rounded-3xl border border-slate-200 bg-white p-10 text-center" : "w-full max-w-lg rounded-3xl border border-slate-200 bg-white p-10 text-center shadow-sm"}>
        <div className="mx-auto h-8 w-8 animate-spin rounded-full border-2 border-slate-300 border-t-slate-950" />
        <p className="mt-4 text-sm text-slate-500">Taking you to secure payment…</p>
      </div>
    );
    if (embedded) return redirectBody;
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        {redirectBody}
      </div>
    );
  }

  if (submittedRef) {
    const successBody = (
      <div className={embedded ? "w-full rounded-3xl border border-slate-200 bg-white p-10 text-center" : "w-full max-w-lg rounded-3xl border border-slate-200 bg-white p-10 text-center shadow-sm"}>
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
        </div>
        <h1 className="mt-6 text-2xl font-bold text-slate-950">{editBookingId ? "Booking Updated" : "Request Submitted"}</h1>
        <p className="mt-2 text-sm text-slate-500">
          {editBookingId
            ? "Your changes have been saved. Our team will review the updated request."
            : "Thank you — your booking request has been received and is pending review. Our team will be in touch shortly."}
        </p>
        <div className="mt-6 rounded-2xl border border-slate-200 bg-slate-50 p-5 text-left">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Reference Number</p>
          <p className="mt-1 text-xl font-bold text-slate-950">{submittedRef}</p>
          <p className="mt-4 text-xs font-semibold uppercase tracking-wide text-slate-500">Status</p>
          <span className="mt-1 inline-block rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-700">Pending Review</span>
        </div>
        {submittedId && (
          <a
            href={`/portal/bookings/${submittedId}/print`}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-6 inline-flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-6 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-50"
          >
            Print This Request
          </a>
        )}
        {!embedded && (
          <p className="mt-4 text-xs text-slate-400">Keep your reference number for future enquiries: (09) 828 5202</p>
        )}
      </div>
    );

    if (embedded) return successBody;

    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        {successBody}
      </div>
    );
  }

  const isLivestock = form.bookingType === "livestock";
  const isFreight = form.bookingType === "freight";
  const isMachinery = form.bookingType === "machinery";
  const selectedSailing = sailings.find(s => s._id === form.sailingId);
  const needsPickup = form.shippingMode === "Door to Port" || form.shippingMode === "Door to Door";
  const needsDelivery = form.shippingMode === "Port to Door" || form.shippingMode === "Door to Door";

  const formBody = (
    <>
      {!embedded && (
        <div className="mb-5">
          <h1 className="text-2xl font-bold tracking-tight text-slate-950">{editBookingId ? "Edit Booking Request" : "Booking Request"}</h1>
          <p className="mt-1.5 text-sm text-slate-500">
            {editBookingId
              ? "Update the details of your pending booking request below."
              : " Pre paid freight or machinery bookings."}
          </p>
          {draftRestored && !editBookingId && (
            <div className="mt-2 flex items-center gap-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800">
              <span>We've restored your unfinished booking from last time.</span>
              <button
                type="button"
                onClick={() => {
                  try { localStorage.removeItem(DRAFT_STORAGE_KEY); } catch (e) {}
                  window.location.reload();
                }}
                className="font-semibold underline hover:text-amber-950"
              >
                Start a new booking instead
              </button>
            </div>
          )}
        </div>
      )}

      {customerId && loadingCustomer && (
        <div className="mb-5 rounded-2xl border border-slate-200 bg-white px-5 py-3 text-sm text-slate-400">
          Loading your account details...
        </div>
      )}

      {loggedInCustomer && !embedded && (
        <div className="mb-5 flex items-center justify-between rounded-2xl border border-emerald-200 bg-emerald-50 px-5 py-3">
          <div className="text-sm">
            <span className="font-semibold text-emerald-800">Logged in as {loggedInCustomer.companyName}</span>
            <span className="text-emerald-600"> — your contact details have been pre-filled below.</span>
          </div>
          <Link to="/portal" className="text-xs font-semibold text-emerald-700 hover:underline shrink-0 ml-4">
            ← Back to Portal
          </Link>
        </div>
      )}

          {/* Booking Type — compact segmented control, not 3 oversized cards.
              Livestock is deliberately excluded from the public (non-portal)
              form — livestock bookings require an account due to the
              Farmer Declaration / compliance requirements, so they're only
              accepted through the logged-in customer portal. */}
          {(() => {
            const availableTypes = embedded ? BOOKING_TYPES : BOOKING_TYPES.filter(t => t.value !== "livestock");
            return (
              <>
                <div className="mb-2 flex gap-1 rounded-2xl border border-slate-200 bg-slate-100 p-1">
                  {availableTypes.map(type => (
                    <button
                      key={type.value}
                      type="button"
                      onClick={() => update("bookingType", type.value)}
                      className={`flex flex-1 items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold transition ${
                        form.bookingType === type.value
                          ? "bg-slate-950 text-white shadow-sm"
                          : "text-slate-500 hover:bg-white/70 hover:text-slate-800"
                      }`}
                    >
                      <type.Icon size={16} className={form.bookingType === type.value ? "text-white" : "text-slate-400"} />
                      {type.label}
                    </button>
                  ))}
                </div>
                {!embedded && (
                  <p className="mb-5 text-xs text-slate-400">
                    Booking livestock? That requires a customer portal account —{" "}
                    <Link to="/portal/login" className="font-semibold text-slate-600 hover:underline">log in here</Link>, or contact us to get set up.
                  </p>
                )}
              </>
            );
          })()}


          {/* Sailing & Route */}
          <Card title="Sailing & Route">
            {(form.sailingId || lockedSailingId) && (
              <div className="mb-5 flex items-center gap-3 rounded-xl bg-slate-50 px-4 py-3.5">
                <span className="text-sm font-bold text-slate-900 shrink-0">{form.origin}</span>
                <div className="h-1.5 w-1.5 rounded-full bg-slate-950 shrink-0" />
                <div className="flex-1 border-t-2 border-dashed border-slate-300" />
                <Anchor size={14} className="text-slate-400 shrink-0" />
                <div className="flex-1 border-t-2 border-dashed border-slate-300" />
                <div className="h-1.5 w-1.5 rounded-full bg-slate-950 shrink-0" />
                <span className="text-sm font-bold text-slate-900 shrink-0">{form.destination}</span>
              </div>
            )}

            {lockedSailingId ? (
              <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3.5">
                <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Requested Sailing</div>
                <div className="mt-0.5 text-sm font-bold text-slate-900">
                  {selectedSailing
                    ? `${selectedSailing.sailingRef} — ${selectedSailing.routeLabel || `${selectedSailing.origin} → ${selectedSailing.destination}`}`
                    : "Loading sailing details..."}
                </div>
              </div>
            ) : (
              <Field label="Requested Sailing">
                <div className="flex gap-2">
                  <select className={`${INPUT_CLASS} flex-1`} value={form.sailingId} onChange={e => updateSailing(e.target.value)}>
                    <option value="">Select Sailing</option>
                    {sailings.map(s => (
                      <option key={s._id} value={s._id}>
                        {s.sailingRef} — {s.routeLabel || `${s.origin} → ${s.destination}`} ({s.etd ? new Date(s.etd).toLocaleDateString("en-NZ", { day: "2-digit", month: "short" }) : "TBC"})
                      </option>
                    ))}
                  </select>
                  {form.sailingId && (
                    <button
                      type="button"
                      onClick={clearSailing}
                      title="Clear selected sailing"
                      className="shrink-0 rounded-xl border border-slate-200 bg-white px-3.5 text-slate-400 hover:border-red-200 hover:bg-red-50 hover:text-red-500 transition"
                    >
                      ✕
                    </button>
                  )}
                </div>
              </Field>
            )}

            {selectedSailing && (
              <div className="mt-3 grid grid-cols-2 gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3.5 sm:grid-cols-4">
                <div>
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Vessel</div>
                  <div className="mt-0.5 text-sm font-semibold text-slate-900">{selectedSailing.vesselName || "TBC"}</div>
                </div>
                <div>
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">ETD</div>
                  <div className="mt-0.5 text-sm font-semibold text-slate-900">
                    {selectedSailing.etd ? new Date(selectedSailing.etd).toLocaleDateString("en-NZ", { day: "2-digit", month: "short", year: "numeric" }) : "TBC"}
                  </div>
                </div>
                <div>
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">ETA</div>
                  <div className="mt-0.5 text-sm font-semibold text-slate-900">
                    {selectedSailing.eta ? new Date(selectedSailing.eta).toLocaleDateString("en-NZ", { day: "2-digit", month: "short", year: "numeric" }) : "TBC"}
                  </div>
                </div>
                <div>
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Booking Cutoff</div>
                  <div className="mt-0.5 text-sm font-semibold text-slate-900">
                    {selectedSailing.bookingCutoff ? new Date(selectedSailing.bookingCutoff).toLocaleDateString("en-NZ", { day: "2-digit", month: "short", year: "numeric" }) : "—"}
                  </div>
                </div>
              </div>
            )}

            {(form.sailingId || lockedSailingId) && form.routeOverride && (
              <div className="mt-5 grid grid-cols-1 gap-5 md:grid-cols-2">
                <Field label="Origin">
                  <select className={INPUT_CLASS} value={form.origin} onChange={e => update("origin", e.target.value)}>
                    <option>Chatham Islands</option>
                    <option>Pitt Island</option>
                    <option>Napier</option>
                    <option>Timaru</option>
                    <option>Auckland</option>
                  </select>
                </Field>

                <Field label="Destination">
                  <select className={INPUT_CLASS} value={form.destination} onChange={e => update("destination", e.target.value)}>
                    <option>Napier</option>
                    <option>Timaru</option>
                    <option>Chatham Islands</option>
                    <option>Pitt Island</option>
                    <option>Auckland</option>
                  </select>
                </Field>
              </div>
            )}

            {form.sailingId && (
              <div className="mt-3 flex items-center justify-between">
                <p className="text-xs text-slate-400">
                  {form.routeOverride
                    ? "Editing route manually — may not match the selected sailing."
                    : "Route is set automatically from the selected sailing, shown above."}
                </p>
                <button
                  type="button"
                  onClick={() => update("routeOverride", !form.routeOverride)}
                  className="text-xs font-semibold text-slate-600 hover:text-slate-900 hover:underline shrink-0 ml-3"
                >
                  {form.routeOverride ? "Use sailing route" : "Edit manually"}
                </button>
              </div>
            )}
          </Card>

          {/* Your Details */}
          <Card title="Your Details" description="Owner, farmer or consignor contact information.">
            <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
              <Field label="Full Name *"><input className={INPUT_CLASS} value={form.senderName} onChange={e => update("senderName", e.target.value)} placeholder="John Smith" /></Field>
              <Field label="Business Name"><input className={INPUT_CLASS} value={form.senderCompany} onChange={e => update("senderCompany", e.target.value)} placeholder="Smith Farm Ltd" /></Field>
              <Field label="Phone *"><input className={INPUT_CLASS} type="tel" value={form.senderPhone} onChange={e => update("senderPhone", e.target.value)} placeholder="+64 21 000 0000" /></Field>
              <Field label="Email"><input className={INPUT_CLASS} type="email" value={form.senderEmail} onChange={e => update("senderEmail", e.target.value)} placeholder="john@example.co.nz" /></Field>
              <div className="md:col-span-2">
                <Field label="Address"><input className={INPUT_CLASS} value={form.senderAddress} onChange={e => update("senderAddress", e.target.value)} placeholder="Farm address" /></Field>
              </div>
            </div>
          </Card>

          {/* Receiver */}
          <Card title="Receiver" description="Who is receiving the stock, freight or equipment at destination.">
            <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
              <Field label="Receiver Name *"><input className={INPUT_CLASS} value={form.receiverName} onChange={e => update("receiverName", e.target.value)} placeholder="Full name" /></Field>
              <Field label="Business Name"><input className={INPUT_CLASS} value={form.receiverCompany} onChange={e => update("receiverCompany", e.target.value)} placeholder="Company or farm name" /></Field>
              <Field label="Phone"><input className={INPUT_CLASS} type="tel" value={form.receiverPhone} onChange={e => update("receiverPhone", e.target.value)} placeholder="+64 21 000 0000" /></Field>
              <Field label="Email"><input className={INPUT_CLASS} type="email" value={form.receiverEmail} onChange={e => update("receiverEmail", e.target.value)} placeholder="receiver@example.co.nz" /></Field>
              <div className="md:col-span-2">
                <Field label="Delivery Address"><input className={INPUT_CLASS} value={form.deliveryAddress} onChange={e => update("deliveryAddress", e.target.value)} placeholder="Street address, city" /></Field>
              </div>
            </div>
          </Card>

          {/* Agent — livestock only */}
          {isLivestock && (
            <Card title="Agent / Consignor" description="If different from owner. Leave blank if not applicable.">
              <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
                <Field label="Agent Name"><input className={INPUT_CLASS} value={form.agentName} onChange={e => update("agentName", e.target.value)} /></Field>
                <Field label="Agent Phone"><input className={INPUT_CLASS} type="tel" value={form.agentPhone} onChange={e => update("agentPhone", e.target.value)} /></Field>
                <Field label="Agent Email"><input className={INPUT_CLASS} type="email" value={form.agentEmail} onChange={e => update("agentEmail", e.target.value)} /></Field>
              </div>
            </Card>
          )}

          {/* Emergency Contacts — livestock only */}
          {isLivestock && (
            <Card title="Emergency Contacts">
              <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
                <Field label="Emergency Contact Name"><input className={INPUT_CLASS} value={form.emergencyContact} onChange={e => update("emergencyContact", e.target.value)} /></Field>
                <Field label="Emergency Phone"><input className={INPUT_CLASS} type="tel" value={form.emergencyPhone} onChange={e => update("emergencyPhone", e.target.value)} /></Field>
                <Field label="Veterinarian Emergency Phone"><input className={INPUT_CLASS} type="tel" value={form.vetEmergencyPhone} onChange={e => update("vetEmergencyPhone", e.target.value)} /></Field>
              </div>
            </Card>
          )}

          {/* ── LIVESTOCK ── */}
          {isLivestock && (
            <>
              <Card title="Animals" description="Add one entry per group of animals — e.g. list your cattle and sheep separately.">
                <div className="space-y-4">
                  {form.livestockItems.map((item, index) => (
                    <div key={index} className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
                      <div className="flex items-center justify-between mb-4">
                        <span className="text-sm font-semibold text-slate-700">Group {index + 1}</span>
                        {form.livestockItems.length > 1 && (
                          <button type="button" onClick={() => removeLivestockItem(index)} className="text-xs font-semibold text-red-500 hover:text-red-700">Remove</button>
                        )}
                      </div>

                      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                        <Field label="Animal Type">
                          <select className={INPUT_CLASS} value={item.livestockType} onChange={e => updateLivestockItem(index, "livestockType", e.target.value)}>
                            <option value="cattle">Cattle</option>
                            <option value="sheep">Sheep</option>
                            <option value="deer">Deer</option>
                            <option value="other">Other</option>
                          </select>
                        </Field>

                        <Field label="Class / Status">
                          <select className={INPUT_CLASS} value={item.animalClass} onChange={e => updateLivestockItem(index, "animalClass", e.target.value)}>
                            <option value="">— Select class —</option>
                            <option value="bull">Bull</option>
                            <option value="cow">Cow</option>
                            <option value="heifer">Heifer</option>
                            <option value="steer">Steer</option>
                            <option value="weaner">Weaner</option>
                            <option value="calf">Calf</option>
                            <option value="ram">Ram</option>
                            <option value="ewe">Ewe</option>
                            <option value="lamb">Lamb</option>
                            <option value="stag">Stag</option>
                            <option value="hind">Hind</option>
                            <option value="fawn">Fawn</option>
                            <option value="mixed">Mixed</option>
                          </select>
                        </Field>

                        <Field label="Head Count *">
                          <input className={INPUT_CLASS} type="number" min="1" value={item.headCount} onChange={e => updateLivestockItem(index, "headCount", e.target.value)} placeholder="0" />
                        </Field>

                        <Field label="Average Weight (kg)">
                          <input className={INPUT_CLASS} type="number" min="0" value={item.avgWeightKg} onChange={e => updateLivestockItem(index, "avgWeightKg", e.target.value)} placeholder="e.g. 450" />
                        </Field>

                        <Field label="Last Fed">
                          <DateTimePicker
                            value={item.lastFed}
                            onChange={(val) => updateLivestockItem(index, "lastFed", val)}
                            placeholder="Select date & time"
                          />
                        </Field>

                        <Field label="Last Watered">
                          <DateTimePicker
                            value={item.lastWatered}
                            onChange={(val) => updateLivestockItem(index, "lastWatered", val)}
                            placeholder="Select date & time"
                          />
                        </Field>
                      </div>

                      <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
                        <Field label="Special Status">
                          <input className={INPUT_CLASS} value={item.specialStatus} onChange={e => updateLivestockItem(index, "specialStatus", e.target.value)} placeholder="e.g. horned, pregnant, unweaned" />
                        </Field>
                        <Field label="Biosecurity Measures">
                          <input className={INPUT_CLASS} value={item.biosecurityMeasures} onChange={e => updateLivestockItem(index, "biosecurityMeasures", e.target.value)} placeholder="e.g. drenched, tagged, TB tested" />
                        </Field>
                      </div>

                      <div className="mt-4">
                        <Field label="Notes">
                          <input className={INPUT_CLASS} value={item.notes} onChange={e => updateLivestockItem(index, "notes", e.target.value)} placeholder="Condition, handling requirements..." />
                        </Field>
                      </div>
                    </div>
                  ))}

                  <button type="button" onClick={addLivestockItem} className="rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50">
                    + Add Another Animal Group
                  </button>
                </div>
              </Card>

              {/* Declarations */}
              <Card title="Farmer Declarations" description="Required by MPI. Must be completed before your booking can be confirmed.">
                <div className="space-y-3">
                  <DeclarationCheck
                    checked={form.fitForTransport}
                    onChange={v => update("fitForTransport", v)}
                    title="Fit for Transport Declaration *"
                    desc="I declare that all animals listed are fit for transport, able to bear weight on all four legs, free from injury, not in the last trimester of pregnancy, not freshly weaned within two weeks, and have been held off pasture and provided with hay and water for at least 12 hours prior to loading."
                  />
                </div>

                <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                  <strong>Note:</strong> The completed declaration form must be provided to the Stockman or Master at least 24 hours prior to loading. Our team will send you the full Livestock Booking & Declaration Form upon confirmation.
                </div>
              </Card>

              {/* Document Upload */}
              <Card title="Documents & Certificates" description="Upload vet certificates, NAIT documents, tag lists, or any other supporting documents.">
                {/* Drop zone */}
                <label className="flex flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed border-slate-300 bg-slate-50 px-6 py-10 cursor-pointer hover:border-slate-400 hover:bg-slate-100 transition">
                  <Paperclip size={28} className="text-slate-400" />
                  <div className="text-center">
                    <div className="text-sm font-semibold text-slate-700">
                      {uploading ? "Uploading..." : "Click to upload files"}
                    </div>
                    <div className="text-xs text-slate-400 mt-1">PDF, images (JPG, PNG), Excel — multiple files supported</div>
                  </div>
                  <input
                    type="file"
                    multiple
                    accept=".pdf,.jpg,.jpeg,.png,.xlsx,.xls,.csv"
                    className="hidden"
                    onChange={handleFileUpload}
                    disabled={uploading}
                  />
                </label>

                {/* File table */}
                {uploadedFiles.length > 0 && (
                  <div className="mt-4 rounded-xl border border-slate-200 overflow-hidden">
                    <div className="grid grid-cols-[1fr_100px_120px_80px_40px] gap-3 px-4 py-2.5 bg-slate-50 border-b border-slate-200 text-xs font-semibold uppercase tracking-wide text-slate-400">
                      <div>File</div>
                      <div>Category</div>
                      <div>Size</div>
                      <div>Status</div>
                      <div></div>
                    </div>
                    {uploadedFiles.map((file, i) => (
                      <div key={i} className={`grid grid-cols-[1fr_100px_120px_80px_40px] gap-3 px-4 py-3 text-sm items-center ${i > 0 ? "border-t border-slate-100" : ""}`}>
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="shrink-0"><FileIcon type={file.type} /></span>
                          <div className="min-w-0">
                            <div className="font-medium text-slate-900 truncate">{file.name}</div>
                            {file.url && (
                              <a href={file.url} target="_blank" rel="noreferrer" className="text-xs text-blue-600 hover:underline">View</a>
                            )}
                          </div>
                        </div>
                        <div>
                          <select
                            value={file.category}
                            onChange={e => setUploadedFiles(prev => prev.map((f, idx) => idx === i ? { ...f, category: e.target.value } : f))}
                            className="w-full rounded-lg border border-slate-200 px-2 py-1 text-xs text-slate-700 outline-none"
                          >
                            <option value="vet_cert">Vet Certificate</option>
                            <option value="nait">NAIT Document</option>
                            <option value="tag_list">Tag List</option>
                            <option value="declaration">Declaration</option>
                            <option value="other">Other</option>
                          </select>
                        </div>
                        <div className="text-slate-400 text-xs">{formatFileSize(file.size)}</div>
                        <div>
                          {file.error
                            ? <span className="text-xs font-semibold text-red-500">Failed</span>
                            : <span className="text-xs font-semibold text-emerald-600">Uploaded</span>
                          }
                        </div>
                        <button
                          type="button"
                          onClick={() => removeFile(i)}
                          className="text-slate-300 hover:text-red-500 transition text-lg leading-none"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <p className="mt-3 text-xs text-slate-400">
                  Documents are attached to your booking request and reviewed by our team. Maximum 10MB per file.
                </p>
              </Card>
            </>
          )}

          {/* ── FREIGHT ── */}
          {isFreight && (
            <Card title="Freight Details" description="Add one or more freight items for this booking.">
              <div className="space-y-4">
                {form.freightItems.map((item, index) => (
                  <div key={index} className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-sm font-semibold text-slate-700">Item {index + 1}</span>
                      <div className="flex items-center gap-3">
                        <div className="inline-flex rounded-lg bg-slate-200 p-0.5">
                          <button
                            type="button"
                            onClick={() => setPackingMode(index, "single")}
                            className={`rounded-md px-2.5 py-1 text-xs font-medium transition ${
                              (item.packingMode || "single") === "single" ? "bg-white text-slate-950 shadow-sm" : "text-slate-500"
                            }`}
                          >
                            Single item
                          </button>
                          <button
                            type="button"
                            onClick={() => setPackingMode(index, "multiple")}
                            className={`rounded-md px-2.5 py-1 text-xs font-medium transition ${
                              item.packingMode === "multiple" ? "bg-white text-slate-950 shadow-sm" : "text-slate-500"
                            }`}
                          >
                            Multiple items
                          </button>
                        </div>
                        {form.freightItems.length > 1 && (
                          <button type="button" onClick={() => removeFreightItem(index)} className="text-xs font-semibold text-red-500 hover:text-red-700">Remove</button>
                        )}
                      </div>
                    </div>

                    {/* Basic details */}
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                      <Field label="Type">
                        <select className={INPUT_CLASS} value={item.unitType} onChange={e => updateFreightItem(index, "unitType", e.target.value)}>
                          <option value="pallet">Pallet</option>
                          <option value="carton">Carton</option>
                          <option value="bundle">Bundle</option>
                          <option value="packet">Packet</option>
                          <option value="loose_item">Loose Item</option>
                          <option value="breakbulk">Break Bulk</option>
                          <option value="other">Other</option>
                        </select>
                      </Field>
                      <Field label="Quantity">
                        {item.packingMode === "multiple" ? (
                          <div className="w-full rounded-xl border border-slate-200 bg-slate-100 px-3.5 py-2.5 text-sm font-semibold text-slate-600">
                            {(item.packingRows || []).reduce((s, r) => s + Number(r.qty || 0), 0)}
                            <span className="ml-1 font-normal text-slate-400">(from packing list)</span>
                          </div>
                        ) : (
                          <input className={INPUT_CLASS} type="number" min="0" value={item.quantity} onChange={e => updateFreightItem(index, "quantity", e.target.value)} placeholder="0" />
                        )}
                      </Field>
                      <Field label="Description">
                        <input className={INPUT_CLASS} value={item.description} onChange={e => updateFreightItem(index, "description", e.target.value)} placeholder="e.g. Farm supplies" />
                      </Field>
                    </div>

                    {/* Weight & dimensions */}
                    <div className="mt-4 pt-4 border-t border-slate-200">
                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-3">Weight &amp; Dimensions</p>

                      {(item.packingMode || "single") === "single" ? (
                        <>
                          <div className="grid grid-cols-2 gap-4 md:grid-cols-5">
                            <Field label="Weight (kg)">
                              <input className={INPUT_CLASS} type="number" min="0" value={item.weightKg} onChange={e => updateFreightItem(index, "weightKg", e.target.value)} placeholder="0" />
                            </Field>
                            <Field label="Length (m)">
                              <input className={INPUT_CLASS} type="number" min="0" step="0.01" value={item.lengthM} onChange={e => updateFreightItem(index, "lengthM", e.target.value)} placeholder="0" />
                            </Field>
                            <Field label="Width (m)">
                              <input className={INPUT_CLASS} type="number" min="0" step="0.01" value={item.widthM} onChange={e => updateFreightItem(index, "widthM", e.target.value)} placeholder="0" />
                            </Field>
                            <Field label="Height (m)">
                              <input className={INPUT_CLASS} type="number" min="0" step="0.01" value={item.heightM} onChange={e => updateFreightItem(index, "heightM", e.target.value)} placeholder="0" />
                            </Field>
                            <Field label="Cube (m³)">
                              <div className="w-full rounded-xl border border-slate-200 bg-slate-100 px-3.5 py-2.5 text-sm font-semibold text-slate-600">
                                {item.cubeM3 || "0"}
                              </div>
                            </Field>
                          </div>
                          <p className="mt-1.5 text-xs text-slate-400"></p>
                        </>
                      ) : (
                        <>
                          <p className="text-xs text-slate-400 mb-2">Add multiple items on this consignment — one row per item, each with its own quantity and dimensions.</p>
                          <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
                            <div className="grid grid-cols-[52px_100px_1fr_90px_80px_80px_80px_110px_24px] gap-2 px-3 py-2 bg-slate-100 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                              <div>Qty</div><div>Package Type</div><div>Description</div><div>Weight (kg)</div><div>Length (m)</div><div>Width (m)</div><div>Height (m)</div><div className="text-right">Line Total</div><div></div>
                            </div>
                            {(item.packingRows || []).map((row, rowIndex) => {
                              const rQty = Number(row.qty || 0);
                              const rWeight = Number(row.weightKg || 0);
                              const rCube = Number(row.lengthM || 0) * Number(row.widthM || 0) * Number(row.heightM || 0);
                              const lineWeight = rQty * rWeight;
                              const lineCube = rQty * rCube;
                              const hasLineTotal = rQty > 0 && (lineWeight > 0 || lineCube > 0);
                              return (
                                <div key={rowIndex} className="grid grid-cols-[52px_100px_1fr_90px_80px_80px_80px_110px_24px] gap-2 px-3 py-2 items-center border-t border-slate-100">
                                  <input type="number" min="0" className="w-full rounded-lg border border-slate-200 px-2 py-1.5 text-sm" value={row.qty} onChange={e => updatePackingRow(index, rowIndex, "qty", e.target.value)} placeholder="0" />
                                  <select className="w-full rounded-lg border border-slate-200 px-1.5 py-1.5 text-xs" value={row.type || "pallet"} onChange={e => updatePackingRow(index, rowIndex, "type", e.target.value)}>
                                    <option value="pallet">Pallet</option>
                                    <option value="carton">Carton</option>
                                    <option value="bundle">Bundle</option>
                                    <option value="packet">Packet</option>
                                    <option value="loose_item">Loose Item</option>
                                    <option value="breakbulk">Break Bulk</option>
                                    <option value="other">Other</option>
                                  </select>
                                  <input type="text" className="w-full rounded-lg border border-slate-200 px-2 py-1.5 text-sm" value={row.description} onChange={e => updatePackingRow(index, rowIndex, "description", e.target.value)} placeholder="e.g. Veterinary supplies" />
                                  <input type="number" min="0" className="w-full rounded-lg border border-slate-200 px-2 py-1.5 text-sm" value={row.weightKg} onChange={e => updatePackingRow(index, rowIndex, "weightKg", e.target.value)} placeholder="0" />
                                  <input type="number" min="0" step="0.01" className="w-full rounded-lg border border-slate-200 px-2 py-1.5 text-sm" value={row.lengthM} onChange={e => updatePackingRow(index, rowIndex, "lengthM", e.target.value)} placeholder="0" />
                                  <input type="number" min="0" step="0.01" className="w-full rounded-lg border border-slate-200 px-2 py-1.5 text-sm" value={row.widthM} onChange={e => updatePackingRow(index, rowIndex, "widthM", e.target.value)} placeholder="0" />
                                  <input type="number" min="0" step="0.01" className="w-full rounded-lg border border-slate-200 px-2 py-1.5 text-sm" value={row.heightM} onChange={e => updatePackingRow(index, rowIndex, "heightM", e.target.value)} placeholder="0" />
                                  <div className="text-right text-xs text-slate-500 leading-tight">
                                    {hasLineTotal ? (
                                      <>
                                        <div className="font-semibold text-slate-800">{lineWeight.toLocaleString(undefined, { maximumFractionDigits: 1 })} kg</div>
                                        <div>{lineCube.toLocaleString(undefined, { maximumFractionDigits: 3 })} m³</div>
                                      </>
                                    ) : (
                                      <span className="text-slate-300">—</span>
                                    )}
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => removePackingRow(index, rowIndex)}
                                    disabled={(item.packingRows || []).length <= 1}
                                    className="text-slate-300 hover:text-red-500 disabled:opacity-30 disabled:cursor-not-allowed"
                                  >
                                    ×
                                  </button>
                                </div>
                              );
                            })}
                            <div className="px-3 py-2 border-t border-slate-100">
                              <button type="button" onClick={() => addPackingRow(index)} className="text-xs font-semibold text-slate-900 hover:text-slate-600">
                                + Add row
                              </button>
                            </div>
                            {(() => {
                              const t = packingRowTotals(item.packingRows);
                              const totalQty = (item.packingRows || []).reduce((s, r) => s + Number(r.qty || 0), 0);
                              return (
                                <div className="flex items-center justify-between px-3 py-2.5 bg-slate-50 border-t border-slate-200">
                                  <span className="text-xs font-semibold text-slate-500">Total across {totalQty} piece{totalQty === 1 ? "" : "s"}</span>
                                  <span className="text-xs font-semibold text-slate-900">
                                    {t.weightKg.toLocaleString(undefined, { maximumFractionDigits: 1 })} kg · {t.cubeM3.toLocaleString(undefined, { maximumFractionDigits: 3 })} m³
                                  </span>
                                </div>
                              );
                            })()}
                          </div>
                        </>
                      )}
                    </div>

                    {/* Temperature */}
                    <div className="mt-4 pt-4 border-t border-slate-200">
                      <label className="block text-sm font-medium text-slate-700 mb-2">Temperature</label>
                      <div className="inline-flex rounded-xl border border-slate-200 bg-white p-1">
                        <button
                          type="button"
                          onClick={() => updateFreightItem(index, "temperatureControl", "ambient")}
                          className={`rounded-lg px-3 py-1.5 text-sm font-semibold transition ${
                            (item.temperatureControl || "ambient") === "ambient"
                              ? "bg-slate-900 text-white"
                              : "text-slate-500 hover:bg-slate-50"
                          }`}
                        >
                          Ambient
                        </button>
                        <button
                          type="button"
                          onClick={() => updateFreightItem(index, "temperatureControl", "chilled")}
                          className={`rounded-lg px-3 py-1.5 text-sm font-semibold transition ${
                            item.temperatureControl === "chilled"
                              ? "bg-sky-600 text-white"
                              : "text-slate-500 hover:bg-slate-50"
                          }`}
                        >
                          ❄ Chilled
                        </button>
                        <button
                          type="button"
                          onClick={() => updateFreightItem(index, "temperatureControl", "frozen")}
                          className={`rounded-lg px-3 py-1.5 text-sm font-semibold transition ${
                            item.temperatureControl === "frozen"
                              ? "bg-indigo-700 text-white"
                              : "text-slate-500 hover:bg-slate-50"
                          }`}
                        >
                          ❄❄ Frozen
                        </button>
                      </div>
                    </div>
                  </div>
                ))}

                <button type="button" onClick={addFreightItem} className="rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50">
                  + Add Another Item
                </button>
              </div>
            </Card>
          )}

          {/* ── MACHINERY ── */}
          {isMachinery && (
            <Card title="Machinery / Vehicle Details" description="Equipment, vehicles and plant for Ro-Ro or crane lift.">
              <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
                <div className="md:col-span-3">
                  <Field label="Description *">
                    <input className={INPUT_CLASS} value={form.machineryDescription} onChange={e => update("machineryDescription", e.target.value)} placeholder="e.g. John Deere tractor, 2019 Toyota Hilux" />
                  </Field>
                </div>
                <Field label="Make"><input className={INPUT_CLASS} value={form.machineryMake} onChange={e => update("machineryMake", e.target.value)} placeholder="e.g. John Deere" /></Field>
                <Field label="Model"><input className={INPUT_CLASS} value={form.machineryModel} onChange={e => update("machineryModel", e.target.value)} placeholder="e.g. 6120M" /></Field>
                <Field label="Weight (kg)"><input className={INPUT_CLASS} type="number" value={form.machineryWeightKg} onChange={e => update("machineryWeightKg", e.target.value)} placeholder="0" /></Field>
                <Field label="Length (m)"><input className={INPUT_CLASS} type="number" step="0.1" value={form.machineryLengthM} onChange={e => update("machineryLengthM", e.target.value)} placeholder="0.0" /></Field>
                <Field label="Width (m)"><input className={INPUT_CLASS} type="number" step="0.1" value={form.machineryWidthM} onChange={e => update("machineryWidthM", e.target.value)} placeholder="0.0" /></Field>
                <Field label="Height (m)"><input className={INPUT_CLASS} type="number" step="0.1" value={form.machineryHeightM} onChange={e => update("machineryHeightM", e.target.value)} placeholder="0.0" /></Field>
              </div>

              <div className="mt-5 space-y-3">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input type="checkbox" checked={form.machineryRoRo} onChange={e => update("machineryRoRo", e.target.checked)} className="h-4 w-4 rounded border-slate-300" />
                  <div>
                    <div className="text-sm font-semibold text-slate-800">Ro-Ro capable (self-propelled or towable)</div>
                    <div className="text-xs text-slate-500">Can be driven or towed aboard — uncheck if crane lift required</div>
                  </div>
                </label>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input type="checkbox" checked={form.machineryHazardous} onChange={e => update("machineryHazardous", e.target.checked)} className="h-4 w-4 rounded border-slate-300" />
                  <div>
                    <div className="text-sm font-semibold text-slate-800">Contains hazardous materials</div>
                    <div className="text-xs text-slate-500">Fuel tanks, batteries, chemicals — our team will contact you for details</div>
                  </div>
                </label>
              </div>

              <div className="mt-5">
                <Field label="Additional Notes">
                  <textarea className={`${INPUT_CLASS} resize-none`} rows={3} value={form.machineryNotes} onChange={e => update("machineryNotes", e.target.value)} placeholder="Special handling, must be steam-cleaned, etc." />
                </Field>
              </div>

              <div className="mt-4 rounded-2xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
                <strong>Note:</strong> Used vehicles and equipment must be thoroughly steam-cleaned and free of dirt and seeds before loading. This is a biosecurity requirement for Chatham Islands shipments.
              </div>
            </Card>
          )}

          {/* General Notes */}
          <Card title="Additional Notes">
            <textarea className={`${INPUT_CLASS} resize-none`} rows={4} value={form.notes} onChange={e => update("notes", e.target.value)} placeholder="Anything else we should know about this booking request..." />
          </Card>

          {/* Estimated Price */}
          {estimate.hasEstimate && (
            <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
              <div className="px-6 py-5">
                <div className="flex items-baseline justify-between">
                  <span className="text-sm font-semibold text-slate-500">Estimated Price</span>
                  <span className="text-2xl font-bold text-slate-950">
                    ${estimate.total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>
              </div>
              <div className="border-t border-slate-100 px-6 py-4 space-y-2.5">
                {estimate.lines.map((line, i) => (
                  <div key={i} className={`flex items-center justify-between text-sm ${line.warning ? "font-semibold text-amber-600" : "text-slate-600"}`}>
                    <span className="capitalize">{line.label}</span>
                    {!line.warning && (
                      <span className="font-medium text-slate-800">${line.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    )}
                  </div>
                ))}
              </div>
              <div className="border-t border-slate-100 bg-slate-50 px-6 py-3">
                <p className="text-xs text-slate-400">
                  Estimate only, based on standard rates. Includes Fuel Adjustment Factor and GST. Excludes port charges and any customer-specific pricing. Final invoice may differ.
                </p>
              </div>
            </div>
          )}

          {/* Owner's Risk Statement — required by the Carrier's insurer.
              No booking may be confirmed without this being executed,
              enforced server-side too (see booking.route.js). */}
          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm px-6 py-6 space-y-5">
            <p className="text-base font-semibold text-slate-900">Owner's Risk Statement</p>

            <blockquote className="text-sm italic leading-relaxed text-slate-600 border-l-2 border-slate-300 pl-4 py-1">
              "These goods are to be carried at owner's risk. This means that the carrier
              will not pay compensation if the goods are lost or damaged unless the carrier
              intentionally loses or damages them."
            </blockquote>

            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
                Full Name
              </label>
              <input
                type="text"
                value={ownersRiskName}
                onChange={(e) => setOwnersRiskName(e.target.value)}
                placeholder="Type your full name"
                className="w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-sm outline-none transition focus:border-slate-950 focus:ring-2 focus:ring-slate-950/10"
              />
            </div>

            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={agreedToOwnersRisk}
                onChange={(e) => setAgreedToOwnersRisk(e.target.checked)}
                className="mt-0.5 h-4 w-4 shrink-0 rounded border-slate-300 text-slate-950 focus:ring-slate-950"
              />
              <span className="text-sm text-slate-700">
                I have read and agree to the Owner's Risk Statement above.
              </span>
            </label>
          </div>

          {/* Terms & Conditions */}
          <label className="flex items-start gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3.5 cursor-pointer">
            <input
              type="checkbox"
              checked={agreedToTerms}
              onChange={(e) => setAgreedToTerms(e.target.checked)}
              className="mt-0.5 h-4 w-4 shrink-0 rounded border-slate-300 text-slate-950 focus:ring-slate-950"
            />
            <span className="text-sm text-slate-700">
              I agree to the{" "}
              <Link
                to="/terms"
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold text-slate-950 underline hover:text-slate-700"
                onClick={(e) => e.stopPropagation()}
              >
                Terms &amp; Conditions
              </Link>
            </span>
          </label>

          {/* Error */}
          {error && (
            <div className="rounded-2xl border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-700">{error}</div>
          )}

          {/* Submit */}
          <div className="flex items-center justify-between gap-4 pt-2">
            <p className="text-xs text-slate-400">
              By submitting this form you agree that all information provided is accurate and complete.
            </p>
            <button
              type="submit"
              disabled={submitting || !agreedToTerms}
              className="rounded-xl bg-slate-950 px-8 py-3 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-50 shrink-0"
            >
              {submitting ? (editBookingId ? "Saving..." : "Submitting...") : (editBookingId ? "Save Changes" : "Submit Booking Request")}
            </button>
          </div>

      </>
  );

  if (embedded) {
    return <form onSubmit={handleSubmit} className="space-y-6">{formBody}</form>;
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="mx-auto max-w-[1100px] px-6 py-10 lg:px-8">
        <form onSubmit={handleSubmit} className="space-y-6">
          {formBody}
        </form>
      </div>
    </div>
  );
}

function guessCategory(filename) {
  const name = filename.toLowerCase();
  if (name.includes("vet") || name.includes("cert")) return "vet_cert";
  if (name.includes("nait") || name.includes("tag")) return "nait";
  if (name.includes("declar")) return "declaration";
  return "other";
}

function FileIcon({ type }) {
  if (!type) return <FileText size={16} className="text-slate-400" />;
  if (type.includes("pdf")) return <FileText size={16} className="text-red-400" />;
  if (type.includes("image")) return <Image size={16} className="text-blue-400" />;
  if (type.includes("spreadsheet") || type.includes("excel") || type.includes("csv")) return <Sheet size={16} className="text-emerald-500" />;
  return <FileText size={16} className="text-slate-400" />;
}

function formatFileSize(bytes) {
  if (!bytes) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function Card({ title, description, children }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className="px-6 py-5 border-b border-slate-100 rounded-t-2xl">
        <h2 className="text-base font-semibold text-slate-950">{title}</h2>
        {description && <p className="mt-0.5 text-sm text-slate-500">{description}</p>}
      </div>
      <div className="px-6 py-5">{children}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</label>
      {children}
    </div>
  );
}

function DeclarationCheck({ checked, onChange, title, desc }) {
  return (
    <label className={`flex items-start gap-3 rounded-xl border px-4 py-3.5 cursor-pointer transition ${checked ? "border-emerald-200 bg-emerald-50" : "border-slate-200 bg-slate-50 hover:bg-slate-100"}`}>
      <input
        type="checkbox"
        checked={checked}
        onChange={e => onChange(e.target.checked)}
        className="mt-0.5 h-4 w-4 rounded border-slate-300 shrink-0"
      />
      <div>
        <div className={`text-sm font-semibold ${checked ? "text-emerald-800" : "text-slate-800"}`}>{title}</div>
        <div className={`text-xs mt-0.5 leading-relaxed ${checked ? "text-emerald-700" : "text-slate-500"}`}>{desc}</div>
      </div>
    </label>
  );
}